/**
 * Resolves access codes for field staff:
 * `communities.access_codes` (JSON list or legacy object) first,
 * then legacy `access_code`, then `access_codes` text.
 */

export type CommunityEmbedForAccess = {
  legal_name?: string | null;
  board_email?: string | null;
  access_codes?: unknown;
  operational_notes?: unknown;
} | null;

const ACCESS_CODE_KINDS = ["intercom", "keypad", "gate", "other"] as const;
type AccessCodeKind = (typeof ACCESS_CODE_KINDS)[number];

const KIND_LABELS: Record<AccessCodeKind, string> = {
  intercom: "Domofon",
  keypad: "Szyfrator",
  gate: "Brama",
  other: "Inny",
};

function unwrapCommunity<T>(c: T | T[] | null | undefined): T | null {
  if (c == null) return null;
  return Array.isArray(c) ? c[0] ?? null : c;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function isKind(v: unknown): v is AccessCodeKind {
  return ACCESS_CODE_KINDS.includes(v as AccessCodeKind);
}

function trimCode(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function formatEntry(kind: AccessCodeKind, code: string, location: string): string {
  const label = KIND_LABELS[kind];
  return location ? `${label} (${location}): ${code}` : `${label}: ${code}`;
}

function parseEntry(item: unknown): { kind: AccessCodeKind; code: string; location: string } | null {
  if (!isRecord(item)) return null;
  const code = trimCode(item.code);
  if (!code) return null;
  const kind = isKind(item.kind) ? item.kind : "other";
  const location = typeof item.location === "string" ? item.location.trim() : "";
  return { kind, code, location };
}

function entriesFromLegacyObject(raw: Record<string, unknown>): { kind: AccessCodeKind; code: string; location: string }[] {
  const entries: { kind: AccessCodeKind; code: string; location: string }[] = [];
  const push = (kind: AccessCodeKind, value: unknown) => {
    const code = trimCode(value);
    if (code) entries.push({ kind, code, location: "" });
  };
  push("intercom", raw.intercom);
  push("keypad", raw.keypad);
  push("gate", raw.gate);
  if (entries.length > 0) return entries;
  push("other", raw.legacyText);
  if (entries.length > 0) return entries;
  push("other", raw.legacySingle);
  return entries;
}

function formatCommunityAccessCodes(codes: unknown): string | null {
  if (codes == null) return null;
  const parsed: { kind: AccessCodeKind; code: string; location: string }[] = [];
  if (Array.isArray(codes)) {
    for (const item of codes) {
      const entry = parseEntry(item);
      if (entry) parsed.push(entry);
    }
  } else if (isRecord(codes)) {
    if (Array.isArray(codes.entries)) {
      for (const item of codes.entries) {
        const entry = parseEntry(item);
        if (entry) parsed.push(entry);
      }
    }
    if (parsed.length === 0) {
      parsed.push(...entriesFromLegacyObject(codes));
    }
  }
  if (parsed.length === 0) return null;
  return parsed.map((e) => formatEntry(e.kind, e.code, e.location)).join("\n");
}

export function resolveStaffAccessCodeDisplay(input: {
  community: CommunityEmbedForAccess | undefined;
  access_code: string | null | undefined;
  access_codes: string | null | undefined;
}): string | null {
  const comm = unwrapCommunity(input.community);
  const fromCommunity = comm ? formatCommunityAccessCodes(comm.access_codes) : null;
  if (fromCommunity) return fromCommunity;
  const single = (input.access_code ?? "").trim();
  if (single.length > 0) return single;
  const blob = (input.access_codes ?? "").trim();
  return blob.length > 0 ? blob : null;
}
